export const categories = [
  {
    id: 'research-papers',
    title: 'The Archive',
    subtitle: 'From Paper to Production',
    description: 'We take seminal research papers in machine learning, quantitative finance, and AI — and transform them into clean, documented, production-ready code. Every implementation is a faithful reconstruction with architecture notes and benchmarks.',
    icon: '📄',
    color: 'copper',
    count: 24,
    path: '/archive',
    features: ['Full Paper Summaries', 'Annotated Source Code', 'Performance Benchmarks', 'Citations & References']
  },
  {
    id: 'social-media',
    title: 'The Decode',
    subtitle: 'Reverse-Engineered Platforms',
    description: 'We reconstruct the inner workings of social media recommendation, ranking, and feed algorithms. Understand how TikTok, Twitter/X, LinkedIn and others curate content — then implement it yourself.',
    icon: '🔥',
    color: 'teal',
    count: 18,
    path: '/decode',
    features: ['Feed Algorithm Models', 'Ranking Systems', 'Engagement Prediction', 'A/B Testing Frameworks']
  },
  {
    id: 'own-algorithms',
    title: 'The Forge',
    subtitle: 'Built from Scratch',
    description: 'Original proprietary algorithms developed in our lab for quantitative trading, data analysis, optimization, and novel problem-solving. Innovation backed by rigorous engineering.',
    icon: '⚡',
    color: 'copper',
    count: 12,
    path: '/forge',
    features: ['Proprietary Trading Systems', 'Custom ML Models', 'Optimization Engines', 'Production Pipelines']
  }
]

export const researchPapers = [
  {
    id: 'attention-is-all-you-need',
    title: 'Attention Is All You Need — Transformer Architecture',
    shortTitle: 'Transformer',
    category: 'nlp',
    authors: ['Vaswani et al.'],
    venue: 'NeurIPS 2017',
    paperUrl: 'https://arxiv.org/abs/1706.03762',
    githubUrl: 'https://github.com/quantalgolabs/transformer-from-scratch',
    abstract: 'Complete from-scratch implementation of the Transformer architecture with multi-head attention, positional encoding, and encoder-decoder stacks. Includes training pipeline on WMT14 dataset.',
    tags: ['NLP', 'Attention', 'PyTorch', 'Deep Learning'],
    difficulty: 'Advanced',
    stars: 1247,
    language: 'Python',
    date: '2024-01-15',
    sections: {
      overview: 'This implementation covers the complete Transformer architecture as described in the seminal "Attention Is All You Need" paper. We build every component from scratch to ensure deep understanding.',
      architecture: [
        'Multi-Head Self-Attention mechanism with scaled dot-product attention',
        'Positional Encoding (sinusoidal + learned variants)',
        'Feed-Forward Networks with GELU activation',
        'Layer Normalization and Residual Connections throughout',
        'Encoder-Decoder architecture with masked attention for autoregressive generation'
      ],
      implementation: 'The code is organized into modular components: attention.py, encoder.py, decoder.py, and transformer.py. Each module is fully documented with docstrings and type hints.',
      results: 'Achieved BLEU score of 27.3 on WMT14 English-German translation task, matching the original paper results within 0.5 BLEU.',
      frontend: 'Interactive visualization of attention weights across layers and heads. Playground for testing custom input sequences with real-time heatmap rendering.',
      backend: 'REST API built with FastAPI for real-time translation inference. Batch processing pipeline with Redis queue and Celery workers.',
      references: [
        { title: 'Original Paper', url: 'https://arxiv.org/abs/1706.03762', type: 'paper' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/transformer-from-scratch', type: 'code' },
        { title: 'Live Demo', url: 'https://demo.quantalgolabs.org/transformer', type: 'demo' }
      ]
    }
  },
  {
    id: 'alpha-zero',
    title: 'Mastering Chess and Shogi by Self-Play — AlphaZero',
    shortTitle: 'AlphaZero',
    category: 'reinforcement-learning',
    authors: ['Silver et al.'],
    venue: 'Science 2018',
    paperUrl: 'https://arxiv.org/abs/1712.01815',
    githubUrl: 'https://github.com/quantalgolabs/alphazero-reproduction',
    abstract: 'End-to-end reproduction of AlphaZero with Monte Carlo Tree Search (MCTS), policy/value networks, and self-play training pipeline. Supports Chess, Go, and custom games.',
    tags: ['RL', 'MCTS', 'Self-Play', 'PyTorch', 'Game AI'],
    difficulty: 'Expert',
    stars: 892,
    language: 'Python',
    date: '2024-02-20',
    sections: {
      overview: 'A faithful reproduction of the AlphaZero algorithm for mastering complex games through self-play reinforcement learning.',
      architecture: [
        'Residual CNN policy/value network with 19 residual blocks',
        'Monte Carlo Tree Search with UCB formula and temperature annealing',
        'Self-play data generation pipeline with asynchronous workers',
        'Distributed training with Ray for multi-GPU scaling',
        'Game-specific rule engines (Chess, Go, custom board games)'
      ],
      implementation: 'Built with PyTorch and Ray for distributed training. Includes a custom game engine with fast move generation using bitboards.',
      results: 'Achieved 95% win rate against Stockfish level 15 after 72 hours of training on 4x A100 GPUs.',
      frontend: 'Interactive chess board with AI opponent. MCTS tree visualization showing explored nodes with depth-coloring.',
      backend: 'Distributed training cluster with Ray. Model serving via TensorRT for sub-millisecond inference.',
      references: [
        { title: 'Original Paper', url: 'https://arxiv.org/abs/1712.01815', type: 'paper' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/alphazero-reproduction', type: 'code' },
        { title: 'Live Demo', url: 'https://demo.quantalgolabs.org/alphazero', type: 'demo' }
      ]
    }
  },
  {
    id: 'lstm-trading',
    title: 'Deep Learning for Stock Prediction — LSTM Network',
    shortTitle: 'LSTM Stock Predictor',
    category: 'finance',
    authors: ['Fischer & Krauss'],
    venue: 'J. Financial Data Science 2018',
    paperUrl: 'https://arxiv.org/abs/1903.01294',
    githubUrl: 'https://github.com/quantalgolabs/lstm-stock-predictor',
    abstract: 'LSTM-based stock price prediction with technical indicators, attention mechanism, and multi-horizon forecasting. Includes backtesting framework and risk metrics.',
    tags: ['Finance', 'LSTM', 'Time Series', 'TensorFlow', 'Trading'],
    difficulty: 'Intermediate',
    stars: 2156,
    language: 'Python',
    date: '2024-03-10',
    sections: {
      overview: 'A practical implementation of LSTM networks for financial time series prediction with real trading signals.',
      architecture: [
        'Bidirectional LSTM with attention layers for temporal feature weighting',
        'Technical indicator feature engineering (RSI, MACD, Bollinger, ATR)',
        'Multi-horizon prediction (1-day, 5-day, 20-day forecasts)',
        'Position sizing and risk management with Kelly criterion',
        'Walk-forward validation framework with expanding window'
      ],
      implementation: 'TensorFlow 2.x implementation with custom loss functions for directional accuracy and MSE.',
      results: '65.3% directional accuracy on S&P 500 constituents with 1.85 Sharpe ratio in backtesting.',
      frontend: 'Real-time stock prediction dashboard with buy/sell signals and portfolio tracking.',
      backend: 'FastAPI backend with TimescaleDB for historical data. Daily prediction scheduler with Celery.',
      references: [
        { title: 'Original Paper', url: 'https://arxiv.org/abs/1903.01294', type: 'paper' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/lstm-stock-predictor', type: 'code' },
        { title: 'Live Dashboard', url: 'https://demo.quantalgolabs.org/lstm-trading', type: 'demo' }
      ]
    }
  },
  {
    id: 'deep-q-learning',
    title: 'Human-Level Control Through Deep RL — DQN',
    shortTitle: 'Deep Q-Network',
    category: 'reinforcement-learning',
    authors: ['Mnih et al.'],
    venue: 'Nature 2015',
    paperUrl: 'https://www.nature.com/articles/nature14236',
    githubUrl: 'https://github.com/quantalgolabs/dqn-atari',
    abstract: 'Complete Deep Q-Network implementation with experience replay, target networks, and Atari 2600 environment wrappers. Includes prioritized replay and double DQN variants.',
    tags: ['RL', 'DQN', 'Atari', 'PyTorch', 'Game AI'],
    difficulty: 'Intermediate',
    stars: 1567,
    language: 'Python',
    date: '2024-01-28',
    sections: {
      overview: 'Reproducing the DQN algorithm that achieved human-level performance on Atari games.',
      architecture: [
        'Convolutional feature extraction from raw RGB pixel frames',
        'Experience Replay Buffer with uniform sampling strategy',
        'Target Network with periodic soft updates',
        'Epsilon-greedy exploration with decay schedule',
        'Frame stacking and preprocessing pipeline (grayscale, resize, normalize)'
      ],
      implementation: 'Built with PyTorch and OpenAI Gym. Includes variants: Double DQN, Dueling DQN, Prioritized Experience Replay.',
      results: 'Exceeded human baseline on 21 of 49 Atari games after 10M training steps.',
      frontend: 'Live game viewer with Q-value heatmaps and training progress dashboard.',
      backend: 'Distributed training with Ray RLlib. Model checkpointing and experiment tracking with Weights & Biases.',
      references: [
        { title: 'Original Paper', url: 'https://www.nature.com/articles/nature14236', type: 'paper' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/dqn-atari', type: 'code' },
        { title: 'Live Demo', url: 'https://demo.quantalgolabs.org/dqn-atari', type: 'demo' }
      ]
    }
  },
  {
    id: 'bert-fine-tuning',
    title: 'BERT: Pre-training of Deep Bidirectional Transformers',
    shortTitle: 'BERT Fine-Tuning',
    category: 'nlp',
    authors: ['Devlin et al.'],
    venue: 'NAACL 2019',
    paperUrl: 'https://arxiv.org/abs/1810.04805',
    githubUrl: 'https://github.com/quantalgolabs/bert-fine-tuning',
    abstract: 'Comprehensive BERT fine-tuning framework for sentiment analysis, NER, and question answering. Includes custom tokenizer, training loops, and inference API.',
    tags: ['NLP', 'BERT', 'Fine-Tuning', 'HuggingFace', 'Transformers'],
    difficulty: 'Intermediate',
    stars: 1834,
    language: 'Python',
    date: '2024-02-15',
    sections: {
      overview: 'A complete BERT fine-tuning pipeline for multiple NLP tasks with best practices.',
      architecture: [
        'Masked Language Modeling (MLM) pre-training objective',
        'Next Sentence Prediction (NSP) for sentence pair tasks',
        'Task-specific fine-tuning heads for classification and token tagging',
        'Layer-wise learning rate decay for stable training',
        'Warmup and linear scheduling with weight decay'
      ],
      implementation: 'HuggingFace Transformers-based with custom training loops and mixed precision training.',
      results: 'F1 score of 0.94 on CoNLL-2003 NER and 92.5% accuracy on SST-2 sentiment analysis.',
      frontend: 'Interactive NLP playground for sentiment analysis, NER, and QA with visualization.',
      backend: 'FastAPI inference server with ONNX Runtime for optimized deployment.',
      references: [
        { title: 'Original Paper', url: 'https://arxiv.org/abs/1810.04805', type: 'paper' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/bert-fine-tuning', type: 'code' },
        { title: 'NLP Playground', url: 'https://demo.quantalgolabs.org/bert-nlp', type: 'demo' }
      ]
    }
  },
  {
    id: 'graph-neural-networks',
    title: 'Semi-Supervised Classification with Graph CNNs',
    shortTitle: 'Graph Convolutional Networks',
    category: 'graph-learning',
    authors: ['Kipf & Welling'],
    venue: 'ICLR 2017',
    paperUrl: 'https://arxiv.org/abs/1609.02907',
    githubUrl: 'https://github.com/quantalgolabs/gcn-implementation',
    abstract: 'Graph Convolutional Network implementation with spectral filtering, message passing, and node classification on citation networks. Includes GAT and GraphSAGE variants.',
    tags: ['Graph ML', 'GCN', 'PyTorch Geometric', 'Node Classification'],
    difficulty: 'Advanced',
    stars: 743,
    language: 'Python',
    date: '2024-03-25',
    sections: {
      overview: 'Implementing Graph Convolutional Networks for learning on graph-structured data.',
      architecture: [
        'Spectral graph convolution with Chebyshev polynomial approximation',
        'Message passing framework for neighborhood aggregation',
        'Graph Attention layers (GAT) with multi-head attention',
        'GraphSAGE sampling and aggregation for inductive learning',
        'Inductive and transductive learning modes'
      ],
      implementation: 'PyTorch Geometric implementation with custom message passing functions.',
      results: '81.5% accuracy on Cora citation network, matching published results.',
      frontend: 'Interactive graph visualization with node classification results and attention weights.',
      backend: 'Graph database with Neo4j. REST API for graph queries and inference.',
      references: [
        { title: 'Original Paper', url: 'https://arxiv.org/abs/1609.02907', type: 'paper' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/gcn-implementation', type: 'code' },
        { title: 'Graph Explorer', url: 'https://demo.quantalgolabs.org/graph-explorer', type: 'demo' }
      ]
    }
  }
]

export const socialMediaAlgorithms = [
  {
    id: 'tiktok-recommendation',
    title: 'TikTok Recommendation Engine — For You Page',
    shortTitle: 'TikTok FYP Algorithm',
    category: 'recommendation',
    platform: 'TikTok',
    abstract: 'Reverse-engineered TikTok recommendation engine with multi-objective ranking, user embedding clustering, and real-time feedback loops.',
    tags: ['Recommendation', 'User Embedding', 'Ranking', 'Real-Time'],
    difficulty: 'Advanced',
    stars: 2456,
    language: 'Python',
    date: '2024-04-01',
    githubUrl: 'https://github.com/quantalgolabs/tiktok-recommendation-engine',
    sections: {
      overview: 'A comprehensive reconstruction of TikTok\'s recommendation algorithm based on publicly available documentation and research.',
      architecture: [
        'Candidate generation with content-based filtering and collaborative signals',
        'Multi-objective ranking model (watch time, likes, shares, comments)',
        'User embedding with temporal attention for interest evolution',
        'Real-time feedback loop with multi-armed bandit algorithms',
        'Diversity and freshness constraints to prevent filter bubbles'
      ],
      implementation: 'TensorFlow + Spark for large-scale recommendation. Redis for real-time user state caching.',
      results: 'Simulated 35% improvement in session watch time over popularity baseline.',
      frontend: 'Interactive recommendation simulator with user profile visualization and content scoring.',
      backend: 'Real-time inference pipeline with Kafka + Redis + TensorFlow Serving.',
      references: [
        { title: 'TikTok Algorithm Documentation', url: 'https://newsroom.tiktok.com/en-us/how-tiktok-recommends-videos', type: 'doc' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/tiktok-recommendation-engine', type: 'code' },
        { title: 'Live Simulator', url: 'https://demo.quantalgolabs.org/tiktok-sim', type: 'demo' }
      ]
    }
  },
  {
    id: 'twitter-ranking',
    title: 'Twitter/X Home Timeline Ranking Algorithm',
    shortTitle: 'Twitter Timeline Ranking',
    category: 'ranking',
    platform: 'Twitter/X',
    abstract: 'Twitter home timeline ranking with heavy ranker, light ranker, and heuristic filtering. Includes engagement prediction and recency boosting.',
    tags: ['Ranking', 'Timeline', 'Engagement Prediction', 'Machine Learning'],
    difficulty: 'Advanced',
    stars: 1876,
    language: 'Python',
    date: '2024-03-15',
    githubUrl: 'https://github.com/quantalgolabs/twitter-ranking-algorithm',
    sections: {
      overview: 'Reconstructing Twitter\'s home timeline algorithm based on the open-sourced ranking code.',
      architecture: [
        'Heavy Ranker: Neural network for engagement prediction',
        'Light Ranker: Logistic regression for real-time scoring',
        'Heuristic filtering: Quality, safety, and diversity checks',
        'Recency boosting with time decay curve',
        'Author-reviewer graph for network features'
      ],
      implementation: 'PyTorch for heavy ranker, scikit-learn for light ranker. Elasticsearch for retrieval.',
      results: 'Replicated the ranking behavior with 89% correlation to reported metrics.',
      frontend: 'Timeline simulator with configurable ranking parameters and real-time scoring.',
      backend: 'Microservices architecture with gRPC for inter-service communication.',
      references: [
        { title: 'Twitter Algorithm Blog', url: 'https://blog.twitter.com/engineering/en_us/topics/open-source/2023/twitter-recommendation-algorithm', type: 'doc' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/twitter-ranking-algorithm', type: 'code' },
        { title: 'Timeline Sim', url: 'https://demo.quantalgolabs.org/twitter-timeline', type: 'demo' }
      ]
    }
  },
  {
    id: 'linkedin-feed',
    title: 'LinkedIn Feed Ranking Algorithm',
    shortTitle: 'LinkedIn Feed Ranking',
    category: 'feed',
    platform: 'LinkedIn',
    abstract: 'LinkedIn feed ranking with multi-objective optimization, professional graph features, and content quality scoring.',
    tags: ['Feed', 'Professional Graph', 'Multi-Objective', 'Ranking'],
    difficulty: 'Advanced',
    stars: 1234,
    language: 'Python',
    date: '2024-02-28',
    githubUrl: 'https://github.com/quantalgolabs/linkedin-feed-ranking',
    sections: {
      overview: 'Modeling LinkedIn\'s feed ranking system with professional network features.',
      architecture: [
        'Multi-objective ranking: engagement, quality, dwell time',
        'Professional graph embeddings (company, school, industry)',
        'Content quality classifier with BERT',
        'Freshness and diversity constraints',
        'A/B testing framework for ranking experiments'
      ],
      implementation: 'PyTorch for ranking models, NetworkX for graph features.',
      results: 'Improved feed engagement metrics by 22% in simulated environment.',
      frontend: 'Professional feed simulator with network visualization.',
      backend: 'Graph database with Dgraph. Real-time feature store with Feast.',
      references: [
        { title: 'LinkedIn Engineering Blog', url: 'https://engineering.linkedin.com/blog', type: 'doc' },
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/linkedin-feed-ranking', type: 'code' },
        { title: 'Feed Simulator', url: 'https://demo.quantalgolabs.org/linkedin-feed', type: 'demo' }
      ]
    }
  }
]

export const ownAlgorithms = [
  {
    id: 'momentum-adaptive-trading',
    title: 'Momentum Adaptive Trading Engine',
    shortTitle: 'Momentum Trading Engine',
    category: 'trading',
    abstract: 'Proprietary adaptive momentum trading algorithm that dynamically adjusts position sizing based on market volatility regime detection.',
    tags: ['Trading', 'Momentum', 'Volatility', 'Python', 'Quantitative'],
    difficulty: 'Expert',
    stars: 3241,
    language: 'Python',
    date: '2024-05-01',
    githubUrl: 'https://github.com/quantalgolabs/momentum-adaptive-trading',
    sections: {
      overview: 'An original adaptive momentum trading system that uses regime-switching models to dynamically adjust strategy parameters.',
      architecture: [
        'Regime detection with HMM (Hidden Markov Models) for volatility states',
        'Dynamic position sizing based on realized volatility and Kelly criterion',
        'Multi-timeframe momentum confirmation (daily, weekly, monthly)',
        'Risk-adjusted position sizing with maximum drawdown controls',
        'Automated execution with slippage modeling and order book analysis'
      ],
      implementation: 'Built with pandas, numpy, and custom C++ extensions for performance-critical path.',
      results: '2.3 Sharpe ratio with 18% annual returns and 12% max drawdown over 5 years of backtesting.',
      frontend: 'Real-time trading dashboard with P&L tracking and regime visualization.',
      backend: 'Event-driven architecture with ZeroMQ for low-latency execution.',
      references: [
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/momentum-adaptive-trading', type: 'code' },
        { title: 'Live Dashboard', url: 'https://demo.quantalgolabs.org/momentum-trading', type: 'demo' }
      ]
    }
  },
  {
    id: 'sentiment-aggregation',
    title: 'Multi-Source Sentiment Aggregation Engine',
    shortTitle: 'Sentiment Aggregator',
    category: 'nlp',
    abstract: 'Real-time sentiment aggregation from Twitter, Reddit, news, and SEC filings with weighted ensemble model and confidence scoring.',
    tags: ['Sentiment', 'NLP', 'Real-Time', 'Ensemble', 'Python'],
    difficulty: 'Advanced',
    stars: 1876,
    language: 'Python',
    date: '2024-04-15',
    githubUrl: 'https://github.com/quantalgolabs/sentiment-aggregation',
    sections: {
      overview: 'A proprietary sentiment aggregation system that combines multiple data sources with weighted confidence scoring.',
      architecture: [
        'Multi-source data ingestion (Twitter, Reddit, News, SEC filings)',
        'Fine-tuned BERT sentiment classifier per source',
        'Weighted ensemble with confidence calibration using Platt scaling',
        'Temporal aggregation with exponential decay for recency weighting',
        'Signal generation for trading decisions with confidence thresholds'
      ],
      implementation: 'Apache Kafka for streaming, PyTorch for inference, PostgreSQL for storage.',
      results: 'Correlation of 0.72 with next-day market direction on S&P 500 constituents.',
      frontend: 'Sentiment heatmap with source breakdown and historical trends.',
      backend: 'Microservices with Kafka Streams. Redis for real-time aggregation.',
      references: [
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/sentiment-aggregation', type: 'code' },
        { title: 'Sentiment Dashboard', url: 'https://demo.quantalgolabs.org/sentiment', type: 'demo' }
      ]
    }
  },
  {
    id: 'portfolio-optimizer',
    title: 'Mean-Variance Portfolio Optimizer with Constraints',
    shortTitle: 'Portfolio Optimizer',
    category: 'finance',
    abstract: 'Advanced portfolio optimization with transaction costs, turnover constraints, and risk budgeting. Includes Black-Litterman and risk parity models.',
    tags: ['Portfolio', 'Optimization', 'Risk', 'Python', 'Finance'],
    difficulty: 'Advanced',
    stars: 1567,
    language: 'Python',
    date: '2024-03-20',
    githubUrl: 'https://github.com/quantalgolabs/portfolio-optimizer',
    sections: {
      overview: 'A comprehensive portfolio optimization framework supporting multiple models and real-world constraints.',
      architecture: [
        'Mean-variance optimization with quadratic programming (CVXPY)',
        'Black-Litterman model with investor views and uncertainty',
        'Risk parity with inverse volatility weighting and leverage constraints',
        'Transaction cost modeling and turnover constraints',
        'Monte Carlo simulation for risk assessment and scenario analysis'
      ],
      implementation: 'CVXPY for optimization, pandas for data, plotly for visualization.',
      results: 'Outperformed equal-weight benchmark by 2.1% annual return with lower volatility.',
      frontend: 'Interactive efficient frontier plot with constraint sliders.',
      backend: 'Optimization API with Celery for async computation.',
      references: [
        { title: 'GitHub Repository', url: 'https://github.com/quantalgolabs/portfolio-optimizer', type: 'code' },
        { title: 'Optimizer Tool', url: 'https://demo.quantalgolabs.org/portfolio-opt', type: 'demo' }
      ]
    }
  }
]

export const allAlgorithms = [
  ...researchPapers.map(a => ({ ...a, type: 'archive' })),
  ...socialMediaAlgorithms.map(a => ({ ...a, type: 'decode' })),
  ...ownAlgorithms.map(a => ({ ...a, type: 'forge' }))
]
